import Image from "@Components/Image";
import OfferChevronDown from "@Icons/OfferChevronDown";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import getImagePath from "@Utils/getImagePath";
import { memo, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  getFeaturedImage,
  getFeaturedImageAtFirst,
  getFormattedGalleryAssets,
  getFormattedTags,
  getMinimumAvailableFromValue,
  getPricingPriceFormatted,
  isAvailable,
  chinaFormattedDate,
} from "../../utils";
import styles from "./RoomTypeSection.module.scss";
import TenanciesSection from "./components/TenanciesSection";
import ViewMoreDetailsModal from "./components/ViewMoreDetailsModal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useRoomTypeContext from "../../context/useRoomTypeContext";
import ThreeDIcon from "@Icons/ThreeDIcon";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import wrapperHOC from "@Utils/wrapperHOC";
import TagV2 from "./components/Tag/TagV2";
import PhotosIcon from "@Icons/PhotosIcon";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import ThreeDIcon2 from "@Icons/ThreeDIcon2";
import PlayIcon from "@Icons/PlayIcon";
import { getVideoThumb } from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import Button from "@Components/Button";
import FallCalendar from "@Icons/FallCalendar";
import useBookClick from "./components/useBookClick";
import { getTranslatedDurationValue } from "./components/TenancyCard/utils";
import { AppState } from "@Store/index";
import useApplyForFallExperiment from "./useApplyForFallExperiment";

type RoomTypeCardProps = {
  id?: string;
  data?: any;
  isDesktop?: boolean;
  activeTab?: string;
  nextRoomId?: string;
};

type AmenityTag = {
  text: string;
  icon: string;
  key: string;
};

const RoomTypeCardV2 = ({
  id,
  data,
  isDesktop = false,
  activeTab = "",
  nextRoomId = "",
}: RoomTypeCardProps) => {
  const customWindowOpen = useCustomWindowOpen();
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { propertyData } = useRoomTypeContext();
  const [showMoreDetailsModal, setShowMoreDetailsModal] = useState(false);
  const [shouldModalScroll, setShouldModalScroll] = useState(false);
  const [isAppliedForFall, setIsAppliedForFall] = useState(false);
  const { onBookClick } = useBookClick(data, customWindowOpen);
  const isApplyForFallExperiment = useApplyForFallExperiment(data, propertyData);
  const { inventoriesData } = useSelector(
    ({ userHistory }: AppState) => ({
      inventoriesData: userHistory.inventoriesData,
    }),
    shallowEqual,
  );

  const openModal = (shouldScroll = false) => {
    gtagEventEmitter({
      action: "room_details_viewed",
      category: PAGE.INVENTORY,
      label: data.name,
      active_tab: activeTab,
    });
    if (shouldScroll && !isDesktop) setShouldModalScroll(true);
    setShowMoreDetailsModal(true);
  };
  const closeModal = () => {
    gtagEventEmitter({
      action: "room_details_closed",
      category: PAGE.INVENTORY,
      label: data.name,
      active_tab: activeTab,
    });
    setShowMoreDetailsModal(false);
    setShouldModalScroll(false);
  };
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const { isWhitelabel } = useWhitelabel();
  const isRootInventoryPricePerPersonEnabled = propertyData?.pricing?.price_per_person || false;
  const isSharedRoom = data?.meta?.types?.includes("shared_room");
  const price = getPricingPriceFormatted(
    { ...data.pricing, price_per_person: isSharedRoom && isRootInventoryPricePerPersonEnabled },
    { price_per_person: true },
  );
  const available = isAvailable(data);
  const minAvailableFrom = getMinimumAvailableFromValue(
    available,
    data?.meta,
    data?.active_children_with_filters,
  );
  const minAvailableFromChinaDate = chinaFormattedDate(minAvailableFrom);
  const isFlexible =
    data?.active_children_with_filters?.length === 0 ||
    (data?.active_children_with_filters || []).some(
      (item: any) => item.available && !item?.meta?.available_from,
    );
  const featuredImage =
    data?.images.length > 0
      ? getFeaturedImage(data?.images)
      : getFeaturedImage(propertyData?.images);
  const tags: AmenityTag[] = getFormattedTags(data, t) || [];
  const excludeTags = [t("Floor")];
  const tagsAfterRemoval = tags.filter((tag) => !excludeTags.includes(tag?.key));
  const featuredImageAsset = [
    {
      ...featuredImage,
      configName: data?.name,
      category: "image",
    },
  ];
  const roomAssets = getFormattedGalleryAssets(data);
  const translatedPrice = getTranslatedDurationValue(price, t, "/");

  const assetsLength = Number(roomAssets?.length || featuredImageAsset?.length);
  const images = data?.images?.length ? getFeaturedImageAtFirst(data?.images) : [featuredImage];
  const shouldShow3D = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_TYPE_3D);
  const virtualView = data?.virtual_views?.map((item: any) => ({
    ...item,
    category: "3D",
    caption: "3D",
    configName: data?.name,
  }));
  const videos = (data?.videos || []).map((item: any) => ({
    ...item,
    category: "videos",
    configName: data?.name,
  }));

  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;
  const floorPlanImages = data?.images?.filter((image: any) => image.type === "floor_plan") || [];

  // const depositLabel = getDepositTextLabel(paymentType);

  // const deposit =
  //   paymentType === "deposit"
  //     ? getPricingDepositFormatted(data.pricing)
  //     : formatTokenPlatformFees(paymentType, propertyData.pricing);

  // Ordered gallery assets for the carousel: images first, then videos, then 3D views.
  const galleryAssets = [
    ...images.map((item: any) => ({ ...item, category: item?.category || "image" })),
    ...videos,
    ...(shouldShow3D ? virtualView : []),
  ];

  const handleFeaturedImageClick = (index: any, label = "image") => {
    gtagEventEmitter({
      action: "room-type-image-clicked",
      category: "room-type",
      label,
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: galleryAssets,
      category: "room-type",
      currentItemIndex: index,
      showViewRoomButton: false,
      config_id: data.id,
    });
  };

  const handleFloorPlanClick = () => {
    gtagEventEmitter({
      action: "room-type-floor-plan-clicked",
      category: "room-type",
      label: "floor_plan",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: floorPlanImages,
      category: "floor-plan",
      currentItemIndex: 0,
      showViewRoomButton: false,
      config_id: data.id,
    });
  };

  const amenityTags: any = [
    { name: t("propertypage.roomtypesectiondynamic.roomtypecard.roomdetailsheading"), values: [] },
    ...(Array.isArray(data?.features) ? data.features : []),
  ];
  tags?.forEach((tag: any) => {
    amenityTags[0].values.push({
      ...tag,
      name: tag?.text,
    });
  });

  const avbFromText = t("propertypage.roomtypesectiondynamic.roomtypecard.availablefrom");
  const is3DPresent = virtualView?.length > 0;
  const isVideoPresent = videos?.length > 0;
  // Start index of each category within galleryAssets (images -> videos -> 3D).
  const videosStartIndex = images.length;
  const virtualViewStartIndex = images.length + videos.length;

  const handleApplyForFallClick = () => {
    gtagEventEmitter({
      action: "clicked_apply_for_fall",
      category: PAGE.INVENTORY,
      room_id: data.id,
      cta_text: "Apply for Fall’26",
    });
    try {
      const tenancyBookObj = JSON.parse(JSON.stringify(data));
      tenancyBookObj.config = JSON.parse(JSON.stringify(data));
      tenancyBookObj.images = [featuredImage];
      onBookClick(
        { ...tenancyBookObj, pricing: {}, config: { ...tenancyBookObj.config, pricing: {} } },
        "Apply For Fall",
        false,
        data.id,
      );
    } catch (err) {
      onBookClick(
        { ...data, pricing: {}, config: { ...data, pricing: {}, images: [featuredImage] } },
        "Apply For Fall",
        false,
        data.id,
      );
    }
  };

  const getFallBtnCTA = () => {
    if (isAppliedForFall) {
      return t("propertypage.components.tenancycard.continueApply1");
    }

    if (isDesktop) {
      return t("propertypage.components.tenancycard.continueApply");
    }

    return t("propertypage.roomtypesectiondynamic.roomtypecard.continueapply2");
  };

  useEffect(() => {
    if (
      Object.keys(inventoriesData.children).length > 0 &&
      inventoriesData.children[String(data.id)]
    ) {
      setIsAppliedForFall(
        ["Waitlisted", "Contacted"].includes(
          inventoriesData.children[String(data.id)]?.label || "",
        ),
      );
    }
  }, [JSON.stringify(inventoriesData)]);

  return (
    <div
      className={formatClassNames(styles.parentContainer, isDesktop && styles.desktop)}
      id={id}
      data-testid="room-types-card"
    >
      <div className={styles.infoSection}>
        <div className={formatClassNames(styles.herov2, isDesktop && styles.desktop)}>
          {isDesktop ? (
            <div
              className={styles.imageContainer}
              onClick={() => handleFeaturedImageClick(0)}
              role="button"
              tabIndex={0}
              onKeyDown={() => handleFeaturedImageClick(0)}
            >
              <Image
                dataTestId="propety-room-type-image"
                src={getImagePath(featuredImage?.path, "small")}
                className={styles.image}
                height={140}
                width={140}
                alt="Room type image"
              />
              <div className={styles.smallIconTagsContainer}>
                <div className={styles.imageCount}>
                  <PhotosIcon width="12px" height="12px" fill="white" />
                  {Number(images.length)}
                </div>
                {isVideoPresent && (
                  <div className={formatClassNames(styles.imageCount)}>
                    <PlayIcon width="12" height="12" fill="white" />
                  </div>
                )}
                {is3DPresent && (
                  <div className={formatClassNames(styles.imageCount)}>
                    <ThreeDIcon2 width={14} height={14} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!isDesktop && (
                <h3
                  data-testid="mobile-propety-room-type-name"
                  className={formatClassNames(styles.titleV2, isWhitelabel ? "wl-title-font" : "")}
                >
                  {data.name || ""}
                </h3>
              )}
              <div className={styles.horizontalScroll}>
                {images.map((img: any, idx: any) => (
                  <div key={`imgs${idx}`} className={styles.imageContainerV2}>
                    <Image
                      dataTestId="mobile-property-room-type-images"
                      src={getImagePath(img?.path, "room-type-card")}
                      className={styles.imageMobile}
                      height={122}
                      width={211}
                      alt="Room type image"
                      addShimmer
                      onClick={() => handleFeaturedImageClick(idx, "image")}
                    />
                    {idx === 0 && (
                      <div className={styles.imageCount}>
                        <PhotosIcon width="12px" height="12px" fill="white" />
                        {Number(roomAssets.length || featuredImageAsset.length)}
                      </div>
                    )}
                  </div>
                ))}
                {videos.map((video: any, idx: any) => (
                  <div
                    key={`video${idx}`}
                    className={styles.imageMobileContainer}
                    onClick={() => handleFeaturedImageClick(videosStartIndex + idx, "video")}
                    role="none"
                  >
                    <Image
                      dataTestId="mobile-property-room-type-videos"
                      src={getVideoThumb(video)}
                      className={styles.imageMobile}
                      height={122}
                      width={211}
                      alt={video?.type || "Room type video"}
                      addShimmer
                    />
                    <div className={styles.overlay}>
                      <PlayIcon width="28" height="28" fill="white" />
                    </div>
                  </div>
                ))}
                {shouldShow3D &&
                  virtualView?.map((item: any, index: any) => (
                    <div
                      key={`3d${index}`}
                      className={styles.imageMobileContainer}
                      onClick={() => handleFeaturedImageClick(virtualViewStartIndex + index, "3D")}
                      role="none"
                    >
                      <Image
                        dataTestId="mobile-property-room-type-3d"
                        src={getImagePath(images[index]?.path, "room-type-card")}
                        className={styles.imageMobile}
                        height={122}
                        width={211}
                        alt={item?.type}
                        addShimmer
                      />
                      <div className={styles.overlay}>
                        <ThreeDIcon />
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
          <div className={styles.info}>
            {isDesktop && (
              <h3
                data-testid="propety-room-type-name"
                className={formatClassNames(
                  isWhitelabel ? "wl-title-font" : "",
                  styles.titleV2,
                  isDesktop && styles.desktop,
                )}
              >
                {data?.name || ""}
              </h3>
            )}
            <div
              className={formatClassNames(
                styles.descriptionv2,
                isDesktop && styles.desktop,
                !available && styles.row,
              )}
            >
              {minAvailableFrom && isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainer}
                >
                  {/* change is needed here */}
                  {avbFromText}:{" "}
                  <span>{config.IS_CHINA_APP ? minAvailableFromChinaDate : minAvailableFrom}</span>
                </span>
              )}
              {isFlexible && available && isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainer}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {minAvailableFrom && !isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainerMobile}
                >
                  {avbFromText} :{" "}
                  <span>{config.IS_CHINA_APP ? minAvailableFromChinaDate : minAvailableFrom}</span>
                </span>
              )}
              {isFlexible && available && !isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainerMobile}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {!available && (
                <span
                  data-testid="property-room-type-tenancy-soldout"
                  className={formatClassNames(styles.soldOutBadge, isDesktop && styles.desktop)}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.soldout")}
                </span>
              )}
              {(isDesktop ||
                (!available && !(isFloorPlansSectionEnabled && floorPlanImages.length > 0))) && (
                <span className={styles.dot}>·</span>
              )}
              {!isDesktop &&
                available &&
                !(isFloorPlansSectionEnabled && floorPlanImages.length > 0) && (
                  <>
                    <span className={styles.dot}>·</span>
                    <span
                      data-testid="property-room-type-price-range"
                      className={styles.priceContainer}
                    >
                      {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                    </span>
                  </>
                )}
              {!isDesktop &&
                !available &&
                !(isFloorPlansSectionEnabled && floorPlanImages.length > 0) && (
                  <span
                    data-testid="property-room-type-price-range"
                    className={styles.priceContainer}
                  >
                    {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                  </span>
                )}
              {isDesktop && (
                <>
                  <span
                    data-testid="property-room-type-price-range"
                    className={styles.priceContainer}
                  >
                    {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                  </span>
                  {isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
                    <>
                      <span className={styles.dot}>·</span>
                      <span
                        className={styles.viewFloorPlanInline}
                        onClick={handleFloorPlanClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                        }}
                        data-testid="room-type-view-floor-plan"
                      >
                        <FloorPlanIcon size="16" />
                        <span>
                          {t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}
                        </span>
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            {!isDesktop && isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
              <div className={styles.priceFloorPlanRow}>
                <span
                  data-testid="property-room-type-price-range"
                  className={styles.priceContainer}
                >
                  {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
                </span>
                <span
                  className={styles.viewFloorPlanInline}
                  onClick={handleFloorPlanClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                  }}
                  data-testid="room-type-view-floor-plan"
                >
                  <FloorPlanIcon size="16" />
                  <span>{t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}</span>
                </span>
              </div>
            )}
            {isDesktop && (
              <div
                className={formatClassNames(
                  styles.amenitiesContainerV2,
                  isDesktop && styles.desktop,
                )}
              >
                {tagsAfterRemoval.slice(0, 6).map((item) => (
                  <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
                ))}
              </div>
            )}
            {isDesktop && (
              <button
                type="button"
                onClick={() => openModal()}
                className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}
              >
                <span
                  data-testid="property-room-type-view-more-details"
                  className={styles.viewMoreText}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoredetails")}
                </span>
                <span className={styles.viewMoreIcon}>
                  <OfferChevronDown height="16" width="16" />
                </span>
              </button>
            )}
            {showMoreDetailsModal && (
              <ViewMoreDetailsModal
                heading={data.name}
                // tenancies={data?.active_children}
                roomTypeData={data}
                onClose={closeModal}
                isSoldOut={!available}
                assetsLength={assetsLength}
                images={images}
                availableFrom={minAvailableFrom}
                isFlexible={isFlexible}
                features={amenityTags}
                featuredImage={featuredImage}
                description={data?.description[0]}
                virtualView={virtualView}
                shouldModalScroll={shouldModalScroll}
              />
            )}
          </div>
        </div>
        {!isDesktop && (
          <div className={styles.amenitiesWithButton}>
            <div className={styles.amenities}>
              {tags.slice(0, 6).map((item) => (
                <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
              ))}
              <button
                type="button"
                onClick={() => openModal()}
                className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}
              >
                <span
                  data-testid="mobile-property-room-type-view-more-details"
                  className={styles.viewMoreText}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoredetails")}
                </span>
                <span className={styles.viewMoreIcon}>
                  <OfferChevronDown height="12" width="12" />
                </span>
              </button>
            </div>
          </div>
        )}
        {isApplyForFallExperiment && (
          <div
            className={formatClassNames(styles.applyForFallContainer, !isDesktop && styles.mobile)}
          >
            <div className={styles.textContainer}>
              <FallCalendar height={isDesktop ? 32 : 24} width={isDesktop ? 32 : 24} />
              <div>
                <div className={styles.titleText}>
                  {isDesktop
                    ? t("propertypage.components.tenancycard.applyForFall2026")
                    : t("propertypage.roomtypesectiondynamic.roomtypecard.applyforfall")}
                </div>
                <div className={styles.subTitleText}>
                  {isDesktop
                    ? t("propertypage.components.tenancycard.saveYourSpotEarly")
                    : t("propertypage.roomtypesectiondynamic.roomtypecard.saveyourspot")}
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className={styles.fallBtn}
              disable={isAppliedForFall}
              onClick={handleApplyForFallClick}
            >
              {getFallBtnCTA()}
            </Button>
          </div>
        )}
        <TenanciesSection
          tenancies={data?.active_children_with_filters}
          roomTypeData={data}
          // soldout tenancies fix by sainath arjun
          isSoldOut={!available}
          featuredImage={featuredImage}
          activeTab={activeTab}
          isDesktop={isDesktop}
          setShowMoreDetailsModal={() => openModal(true)}
          nextRoomId={nextRoomId}
        />
      </div>
    </div>
  );
};

export default wrapperHOC(memo(RoomTypeCardV2), {
  componentName: "RoomTypeCardV2-RoomTypeSection-PropertyPage",
  showForChina: true,
});
